// ============================================================================
// Position form — New Position modal (§1.3) + Edit slide-over (§4.4).
//
// Create mode: full field set, INSERTs a hiring_requests row via createPosition.
// Edit mode: same fields, all editable, plus a "Move to Stage" dropdown at the
// top for advancing without dragging. Moving to the Onboarding stage is routed
// up to the parent (onMoveStage) which opens the hand-off modal instead of
// writing the status directly.
//
// One card = one requisition.
//
// 2026-09-03: the building is asked FIRST, from a global tiered picker with a
// search box (Ben's ruling), and the customer is DERIVED from it. The old
// comment here said there was no facility picker at all; there has been one
// since 2026-05-28 and it was customer-scoped until today.
// ============================================================================

import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import { FacilityTierPicker } from './facility-tier-picker';
import {
  HIRING_STAGES,
  HIRING_STAGE_LABELS,
  HIRING_POSTING_SOURCES,
  type HiringRequestStatus,
  type HiringUrgency,
} from '../../shared/types';
import {
  createPosition,
  updatePosition,
  type AccountOption,
  type FacilityOption,
  type HiringRequestRow,
} from './hooks/use-hiring-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { FormExplainer } from '../../shared/explainer';
import { errorMessage } from '../../shared/errors';

const URGENCY_OPTIONS: HiringUrgency[] = ['High', 'Medium', 'Low'];

interface Props {
  mode: 'create' | 'edit';
  row?: HiringRequestRow;
  accounts: AccountOption[];
  facilities: FacilityOption[];
  myEid: string | null;
  onClose: () => void;
  /** `seats` is how many requests the database opened — one per person. */
  onCreated?: (requestId: string, seats: number) => void;
  onSaved?: () => void;
  /** Edit only: "Add another opening like this" (item 621). */
  onAddOpening?: (row: HiringRequestRow) => Promise<void>;
  onMoveStage?: (row: HiringRequestRow, next: HiringRequestStatus) => void;
  onDelete?: (row: HiringRequestRow) => void;
  onArchive?: (row: HiringRequestRow) => void;
  canDelete?: boolean;
  canHardDelete?: boolean;
}

export default function PositionForm({
  mode,
  row,
  accounts,
  facilities,
  myEid,
  onClose,
  onCreated,
  onSaved,
  onAddOpening,
  onMoveStage,
  onDelete,
  onArchive,
  canDelete,
  canHardDelete,
}: Props) {
  const isEdit = mode === 'edit';
  const [title, setTitle] = useState(row?.title ?? '');
  const [accountId, setAccountId] = useState(row?.account_id ?? '');
  const [facilityId, setFacilityId] = useState(row?.facility_id ?? '');
  const [headcount, setHeadcount] = useState(String(row?.headcount ?? 1));
  const [urgency, setUrgency] = useState<HiringUrgency>(
    (row?.urgency_label as HiringUrgency) ?? 'Medium',
  );
  const [shift, setShift] = useState(row?.shift ?? '');
  const [postingSource, setPostingSource] = useState(row?.posting_source ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [stage, setStage] = useState<HiringRequestStatus>(
    (row?.status as HiringRequestStatus) ?? 'Open',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active accounts only, sorted by name (§1.3 + §4.6).
  const activeAccounts = useMemo(
    () =>
      accounts
        .filter((a) => a.account_status === 'Active' || a.account_id === accountId)
        .sort((a, b) => (a.account_name ?? '').localeCompare(b.account_name ?? '')),
    [accounts, accountId],
  );

  // ⛔ BEN'S RULING, 2026-09-03 — THE BUILDING IS CHOSEN FIRST, FROM ALL OF
  // THEM. This used to be `facilities.filter(f => f.account_id === accountId)`
  // with the account picker above it and the building picker disabled until you
  // had answered it. That is not how the person filling this form thinks: an AM
  // thinks "I need another cook for Research Medical Center", not "which
  // customer owns Research Medical Center". It also made the ruling's tiering
  // pointless, because tiers over one customer's two buildings order nothing,
  // and it meant a building was reachable only through its customer.
  //
  // So the list is global and the ACCOUNT IS DERIVED from the building. The
  // account picker stays for the account-wide requisition — 9 of the 10
  // facility-less requests on record carry an account and no building, so that
  // shape is real and is not being taken away.
  //
  // In edit mode a row may point at a building the picker's source no longer
  // returns; it is prepended so the current selection still renders rather than
  // silently blanking.
  const pickerFacilities = useMemo(() => {
    if (facilityId && !facilities.some((f) => f.facility_id === facilityId)) {
      return [
        {
          facility_id: facilityId,
          facility_name: row?.facility_name ?? facilityId,
          account_id: accountId || null,
        } as FacilityOption,
        ...facilities,
      ];
    }
    return facilities;
  }, [facilities, facilityId, accountId, row?.facility_name]);

  // Choosing a building answers the customer question. Nothing is invented —
  // the building already carries its account and this reads it.
  const onPickFacility = useCallback(
    (nextFacilityId: string) => {
      setFacilityId(nextFacilityId);
      if (!nextFacilityId) return;
      const acct = facilities.find((f) => f.facility_id === nextFacilityId)?.account_id ?? '';
      if (acct) setAccountId(acct);
    },
    [facilities],
  );

  function validate(): string | null {
    if (!title.trim()) return 'Enter a role title.';
    // One clause per missing thing, and it names what to do. Choosing a
    // building fills the customer in, so the only way to be here with no
    // customer is an account-wide requisition with nothing chosen at all.
    if (!accountId) {
      return facilityId
        ? 'That building has no customer on record — pick the customer, or tell the COO the building needs one.'
        : 'Choose the building this hire is for, or pick a customer for an account-wide requisition.';
    }
    if (!isEdit) {
      const h = Number(headcount);
      if (!Number.isFinite(h) || h <= 0) return 'Enter a headcount greater than zero.';
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && row) {
        await updatePosition(row.request_id, {
          title,
          account_id: accountId,
          facility_id: facilityId || null,
          urgency_label: urgency,
          shift,
          posting_source: postingSource || null,
          notes,
        });
        onSaved?.();
        onClose();
      } else {
        const res = await createPosition({
          title,
          account_id: accountId,
          facility_id: facilityId || null,
          headcount: Math.round(Number(headcount)),
          urgency_label: urgency,
          shift: shift || null,
          posting_source: postingSource || null,
          notes: notes || null,
          status: stage,
          requested_by: myEid,
        });
        if (res?.request_id) onCreated?.(res.request_id, Math.round(Number(headcount)));
        onClose();
      }
    } catch (err) {
      console.error('[position-form] save failed', err);
      setError(errorMessage(err, 'Could not save position.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-tcs-text">
              {isEdit ? 'Edit position' : 'New position'}
            </h2>
            {isEdit && row && (
              <p className="truncate text-xs text-tcs-text-muted">{row.request_id}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* 2026-07-06 — collapsible "how this works" explainer (DB-backed). */}
        <div className="mt-3">
          <FormExplainer surfaceKey="form:hiring-request" />
        </div>

        {/* Move to Stage (edit only) — advancing without dragging. */}
        {isEdit && row && onMoveStage && (
          <div className="mt-4 rounded-md border border-tcs-border bg-tcs-bg-muted p-3">
            <Label>Move to stage</Label>
            <select
              value={row.status ?? 'No_Status'}
              onChange={(e) =>
                onMoveStage(row, e.target.value as HiringRequestStatus)
              }
              className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm focus:border-tcs-border-input focus:outline-none"
            >
              {HIRING_STAGES.map((s) => (
                <option key={s} value={s}>
                  {HIRING_STAGE_LABELS[s]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-tcs-text-muted">
              Moving to <strong>Onboarding</strong> opens the hand-off form.
            </p>
          </div>
        )}

        <div className="mt-4 space-y-4">
          <Field label="Role title *">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Night cleaner"
              className={inputCls}
            />
          </Field>

          {/* The building comes FIRST and it is the whole list. Hospitals at
              the top, then the other sites we staff hourly, then everything
              else — with a search box across all three. */}
          <Field label="Building *">
            <FacilityTierPicker
              facilities={pickerFacilities}
              value={facilityId}
              onChange={onPickFacility}
            />
          </Field>

          {/* Derived from the building, and only editable when there is no
              building — which is the account-wide requisition. Never a
              prerequisite for choosing a building. */}
          <Field
            label={
              facilityId
                ? 'Customer (from the building)'
                : 'Customer * — required for an account-wide requisition'
            }
          >
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              disabled={!!facilityId}
              className={inputCls}
            >
              <option value="">— Select —</option>
              {activeAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_name ?? a.account_id}
                </option>
              ))}
            </select>
          </Field>

          {/* A request is one seat (item 621). On a NEW request this is how
              many people are wanted, and the database opens one request per
              person. On an existing seat there is no count to edit — "Add
              another opening like this" below is the one way to ask for more,
              and a seat nobody needs is Disregarded on its own card. */}
          <div className={isEdit ? '' : 'grid grid-cols-2 gap-3'}>
            {!isEdit && (
              <Field label="# of hires *">
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={headcount}
                  onChange={(e) => setHeadcount(e.target.value)}
                  className={inputCls}
                />
              </Field>
            )}
            <Field label="Priority *">
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value as HiringUrgency)}
                className={inputCls}
              >
                {URGENCY_OPTIONS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Shift schedule">
              <input
                value={shift}
                onChange={(e) => setShift(e.target.value)}
                placeholder="e.g. M–F 6p–10p"
                className={inputCls}
              />
            </Field>
            <Field label="Posting source">
              <select
                value={postingSource}
                onChange={(e) => setPostingSource(e.target.value)}
                className={inputCls}
              >
                <option value="">— None —</option>
                {HIRING_POSTING_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {!isEdit && (
            <Field label="Stage *">
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value as HiringRequestStatus)}
                className={inputCls}
              >
                {HIRING_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {HIRING_STAGE_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Notes">
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Shift constraints, context, anything HR should know."
              className={inputCls}
            />
          </Field>
        </div>

        {/* Not offered on the historical multi-seat requests that predate the
            split: raising one would rewrite its count, and they are left as
            they were recorded. */}
        {isEdit && row && onAddOpening && (row.headcount ?? 1) === 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-tcs-border bg-tcs-bg-muted p-3">
            <p className="text-xs text-tcs-text-secondary">
              {row.seat_count != null
                ? `This is opening ${row.seat_number} of ${row.seat_count}.`
                : 'This request is for one person.'}{' '}
              Need one more person for the same job?
            </p>
            <button
              type="button"
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                setError(null);
                try {
                  await onAddOpening(row);
                  onClose();
                } catch (err) {
                  console.error('[position-form] add opening failed', err);
                  setError(errorMessage(err, 'Could not add another opening.'));
                  setSubmitting(false);
                }
              }}
              className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:bg-tcs-bg-muted disabled:opacity-50"
            >
              Add another opening like this
            </button>
          </div>
        )}

        {error && (
          <p className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-between gap-2">
          <div>
            {/* 2026-07-23 (P1 T5) — destructive hard delete only for
                superuser/executive (admin_force_delete is server-gated).
                Other editors get the "Mark as no longer needed" archive path
                → status='Disregarded' (a normal RLS-checked status write). */}
            {isEdit && row && canHardDelete && onDelete ? (
              <button
                type="button"
                onClick={() => onDelete(row)}
                className="rounded-md border border-st-danger-border px-3 py-2 text-sm font-medium text-st-danger-fg hover:bg-st-danger-bg"
              >
                Delete permanently
              </button>
            ) : isEdit && canDelete && row && onArchive ? (
              <button
                type="button"
                onClick={() => onArchive(row)}
                className="rounded-md border border-tcs-border px-3 py-2 text-sm font-medium text-tcs-text-secondary hover:bg-tcs-bg-muted"
              >
                Mark as no longer needed
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
            >
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create position'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
      {children}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
